import { Box, Card, CardHeader, FormHelperText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Add } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { Button, CheckboxWithLabel, ProgressNoteAlert, useHelperTextClasses } from '@uplift/ui-v2';
import { Form, useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useClinicalNotesContext } from '../../clinical-notes.context';
import { SectionContainerContent } from '../../components';
import { ClinicalNotesFormType } from '../../types';
import { DiagnosisSearchModal } from '../../../../components/diagnosis-select/diagnosis-search-modal.modal';
import { DiagnosisSearch } from '../../../../components/diagnosis-select/diagnosis-search.component';
import { DiagnosisSelectedChips } from '../../../../components/diagnosis-select/diagnosis-selected-chips.component';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole } from '@uplift/types';

export type SelectedDiagnosesHashType = { [key: string]: boolean };

export const Diagnosis: React.VFC = () => {
  const l10n = useL10n();
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const { state: { diagnoses } } = useClinicalNotesContext();
  const formik = useFormikContext<ClinicalNotesFormType>();
  const selectedDiagnosesIds = formik.values.diagnosis;
  const [ visited, setVisited ] = useState(false);
  const [ open, setOpen ] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setVisited(true);
  };

  const onDiagnosisSelectHandler = (diagnosisId: string) => {
    formik.setValues((prev) => ({
      ...prev,
      diagnosis: [ ...prev.diagnosis, diagnosisId ]
    }));
    handleClose();
  };

  const onDiagnosisRemoveHandler = (diagnosisId: string) => {
    formik.setValues((prev) => ({
      ...prev,
      diagnosis: prev.diagnosis.filter((id) => (id !== diagnosisId)),
    }));
  };

  // hash to optimize array searches
  const selectedDiagnosesHash = useMemo(() =>
    selectedDiagnosesIds.reduce((r, v) =>
      ({ ...r, [v]: true }),
    {} as { [key: string]: boolean }),
  [ selectedDiagnosesIds ]);

  const selectedDiagnoses = useMemo(() => {
    return diagnoses.filter((diagnosis) => {
      return (diagnosis.id in selectedDiagnosesHash);
    });
  }, [ selectedDiagnosesHash, diagnoses ]);

  const hasDiagnoses = Boolean(selectedDiagnosesIds.length);

  const error = formik.errors?.diagnosis ?? null;
  const showError = !!error && (visited || !!formik.touched?.diagnosis);

  return (
    <Form name="diagnosis">
      <SectionContainerContent className="fs-exclude">
        {hasDiagnoses && (
          <Box mb={3}>
            <DiagnosisSelectedChips
              selectedDiagnoses={selectedDiagnoses}
              onDiagnosisRemove={onDiagnosisRemoveHandler}
            />
          </Box>
        )}
        <Box ml={hasDiagnoses ? -1 : 0}>
          <Button
            onClick={() => setOpen(true)}
            color="secondary"
            variant={hasDiagnoses ? 'text' : 'contained'}
            startIcon={<Add />}
          >
            {l10n.clinicalNotes.addDiagnosis}
          </Button>
          {showError && (
            <Box mt={3}>
              <FormHelperText className={helperTextClasses.root} error={true}>
                {error}
              </FormHelperText>
            </Box>
          )}
        </Box>
      </SectionContainerContent>
      {open && (
        <DiagnosisSearchModal onClose={handleClose}>
          <DiagnosisSearch
            color="secondary"
            diagnoses={diagnoses}
            selectedDiagnosesHash={selectedDiagnosesHash}
            onDiagnosisSelect={onDiagnosisSelectHandler}
          />
        </DiagnosisSearchModal>
      )}
    </Form>
  );
};

export const Reminder: React.VFC = () => {
  const l10n = useL10n();

  return (
    <ProgressNoteAlert title={l10n.clinicalNotes.reminder}>

      <Box style={{ display: 'inline-block' }}>
        <Typography variant="body2" color="textPrimary">{l10n.clinicalNotes.reminderText.line1}&nbsp;
          <Typography component="strong" fontWeight={500} variant="body2" color="textPrimary">{l10n.clinicalNotes.reminderText.line2}</Typography>
        </Typography>
      </Box>
    </ProgressNoteAlert>
  );
};

export type DiagnosisSectionTypes = {
  paperClassName: string;
  isDiagnosisReviewRequired?: boolean;
};

export const DiagnosisSection: React.VFC<DiagnosisSectionTypes> = ({ paperClassName, isDiagnosisReviewRequired }) => {
  const { classes } = useStyles();
  const me = useCurrentuser();

  return (
    <Box className={classes.section}>
      <Box flex={3} className={paperClassName}>
        <Card variant="outlined">
          <CardHeader
            className={classes.header}
            title="Diagnosis"
            action={isDiagnosisReviewRequired
              && <CheckboxWithLabel showError color="secondary" name="isDiagnosisReviewed" label="Is reviewed" />
            }
            slotProps={{
              title: { variant: 'h5', className: classes.headerTitle }
            }}
          />
          <Diagnosis />
        </Card>
      </Box>
      <Box flex={1} maxWidth="316px" display="flex" flexDirection="column" gap="16px">
        {me.medicalRole === ProviderMedicalRole.therapist && <Reminder />}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    display: 'flex',
    gap: '32px'
  },
  header: {
    padding: theme.spacing(2, 3),
    boxShadow: `inset 0 -1px 0 ${theme.palette.grey[300]}`
  },
  headerTitle: {
    fontWeight: 450,
    fontSize: '20px'
  },
}));
